import { ChatDashboard, ChatDashboardFilters } from '@if/mui'
import type { ChatDashboardFilters as ChatDashboardFiltersType, ChatDashboardEntry } from '@if/core/types'
import { ECreditApplicationStatus, EMessageStatusFilter, EMessageType, ESortOrder } from '@if/core/types'
import type { GridPaginationModel } from '@mui/x-data-grid'
import {
  useGetMessageListEntries,
  useInvalidateDashboard,
} from '@src/data/api/chat-dashboard/chat-dashboard-api'
import { getSignalRConnection, useSignalRConnectionReady } from '@src/hooks/useChatMessageSignalR'
import useFeatureFlags from '@src/hooks/config-hooks'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { usePersistedFilters } from './usePersistedFilters'

const CURRENT_USER_PARTICIPANT_TYPE = EMessageType.External

const defaultFilters: ChatDashboardFiltersType = {
  searchTerm: undefined,
  messageStatus: EMessageStatusFilter.Unread,
  sortOrder: ESortOrder.OldestFirst,
}

const ChatDashboardPage = () => {
  const { flags, isFetchingFeatureSwitch } = useFeatureFlags()
  const { t } = useTranslation()

  const [filters, setFilters] = usePersistedFilters(defaultFilters)
  const [selectedPrograms, setSelectedPrograms] = useState<string[] | undefined>(undefined)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  })

  const offset = paginationModel.page * paginationModel.pageSize
  const limit = paginationModel.pageSize

  const filtersWithPrograms = useMemo<ChatDashboardFiltersType>(
    () => ({
      ...filters,
      financingPrograms: selectedPrograms as ChatDashboardFiltersType['financingPrograms'],
    }),
    [filters, selectedPrograms],
  )

  const [entries, isLoading] = useGetMessageListEntries(filtersWithPrograms, offset, limit)

  const handleFiltersChange = useCallback(
    (updater: (prev: ChatDashboardFiltersType) => ChatDashboardFiltersType) => {
      setFilters(updater)
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    },
    [setFilters],
  )

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setFilters((prev) => ({ ...prev, searchTerm: searchTerm || undefined }))
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    },
    [setFilters],
  )

  const handleProgramsChange = useCallback((programs: string[] | undefined) => {
    setSelectedPrograms(programs)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [])

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model)
  }, [])

  const getRowClassName = useCallback(
    (params: { row: ChatDashboardEntry }) => {
      const { isUnread, type, applicationStatus } = params.row
      if (
        applicationStatus === ECreditApplicationStatus.Cancelled ||
        applicationStatus === ECreditApplicationStatus.Expired
      ) {
        return 'inactive-row'
      }
      return isUnread && type !== CURRENT_USER_PARTICIPANT_TYPE ? 'unread-row' : ''
    },
    [],
  )

  const handleConversationSelect = useCallback(
    (entry: ChatDashboardEntry) => {
      window.open(`/chat-dashboard/${entry.financingProgram}/${entry.id}`, '_blank')
    },
    [],
  )

  // Listen for real-time dashboard updates via SignalR
  const signalRReady = useSignalRConnectionReady()
  const invalidateDashboard = useInvalidateDashboard()
  useEffect(() => {
    const connection = getSignalRConnection()
    if (!connection) return

    connection.on('messageUpdated', invalidateDashboard)

    return () => {
      connection.off('messageUpdated', invalidateDashboard)
    }
  }, [invalidateDashboard, signalRReady])

  if (!isFetchingFeatureSwitch && !flags.UseInstantMessage) return <Navigate to="/" replace />

  return (
    <ChatDashboard
      entries={entries}
      totalCount={entries.length}
      isLoading={isLoading}
      searchTerm={filters.searchTerm ?? ''}
      onSearchChange={handleSearchChange}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      onConversationSelect={handleConversationSelect}
      getRowClassName={getRowClassName}
      title={t('instantMessage.chatDashboardTitle')}
      filtersSlot={
        <ChatDashboardFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          showLanguageFilter
          selectedPrograms={selectedPrograms}
          onProgramsChange={handleProgramsChange}
        />
      }
    />
  )
}

export default memo(ChatDashboardPage)
